import React, { useEffect, useRef, useState, useMemo } from 'react';
import { User, Bot } from 'lucide-react';
import { Volume2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import type { ChatMessageProps, SuggestionItem } from '../../types/chat';
import ConfidenceScore from './ConfidenceScore';
import SourceCitations from './SourceCitations';
import DocumentsList from './DocumentsList';
import CopyButton from './CopyButton';
import { SuggestionsList } from './SuggestionsList';
import { SuggestionCard } from './SuggestionCard';
import { SearchRecommendations } from './SearchRecommendations';
import { useSpeaking } from '../../contexts/SpeakingContext';
import { upsertMessage } from '../../services/chatStorage';
import { useLocalStorage } from '../../hooks/useLocalStorage';

interface ExtendedChatMessageProps extends ChatMessageProps {
  onSelectSuggestion?: (suggestion: SuggestionItem) => void;
  onSearchRecommendation?: (query: string) => void;
  onScrollToBottom?: () => void;
  startSignal?: number;
}

const ChatMessage: React.FC<ExtendedChatMessageProps> = ({ 
  message, 
  showAvatar = true, 
  showTimestamp = true,
  onSelectSuggestion,
  onSearchRecommendation,
  onScrollToBottom,
  startSignal
}) => {
  const isUser = message.sender === 'user';
  const speaking = useSpeaking();
  const [speakingError, setSpeakingError] = useState<string | null>(null);
  const [autoSpeak] = useLocalStorage<boolean>('kiosk-auto-speak', true);
  const autoSpokenRef = useRef(false);
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Wrapper function to handle suggestion selection with auto-scroll
  const handleSuggestionSelect = (suggestion: SuggestionItem) => {
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
      // Scroll to bottom after a brief delay to allow the new message to be added
      if (onScrollToBottom) {
        setTimeout(() => {
          onScrollToBottom();
        }, 100);
      }
    }
  };

  const renderSuggestions = () => {
    if (!message.suggestions || message.suggestions.length === 0 || !onSelectSuggestion) {
      return null;
    }

    // For direct_answer_with_suggestions, show as cards without snippets
    if (message.responseType === 'direct_answer_with_suggestions') {
      return (
        <div className="mt-4">
          <div className="mb-3">
            <h4 className="text-sm font-medium text-gray-700">Chủ đề liên quan</h4>
            <p className="text-xs text-gray-500">Nhấp để xem thông tin chi tiết</p>
          </div>
          <div className="grid gap-2">
            {message.suggestions.slice(0, 2).map((suggestion, index) => (
              <SuggestionCard
                key={`${suggestion.id}-${index}`}
                suggestion={suggestion}
                onSelect={handleSuggestionSelect}
                showSnippet={false}
              />
            ))}
          </div>
        </div>
      );
    }

    // For suggestions response type, show title-only list
    if (message.responseType === 'suggestions') {
      return (
        <div className="mt-4">
          <div className="mb-3">
            <h4 className="text-sm font-medium text-gray-700">
              {message.showSuggestionsOnlyTitles ? 'Chủ đề liên quan' : 'Gợi ý làm thủ tục'}
            </h4>
            {/* <p className="text-xs text-gray-500">
              {message.showSuggestionsOnlyTitles 
                ? 'Click any topic to get detailed information'
                : 'Select a suggestion to continue'
              }
            </p> */}
          </div>
          <div className="grid gap-2">
            {message.suggestions.slice(0, 3).map((suggestion, index) => (
              <SuggestionCard
                key={`${suggestion.id}-${index}`}
                suggestion={suggestion}
                onSelect={handleSuggestionSelect}
                showSnippet={!message.showSuggestionsOnlyTitles}
              />
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const getTextToSpeak = (): string => {
    if (message.summary && (message.responseType === 'suggestions' || message.responseType === 'search_recommended')) {
      return message.summary;
    }
    return message.content || '';
  };

  const textToSpeak = useMemo(() => getTextToSpeak().trim(), [message.content, message.summary, message.responseType]);

  const handleSpeak = async () => {
    const text = textToSpeak;
    if (!text) return;
    setSpeakingError(null);
    try {
      await speaking.speakText(text, message.id);
    } catch (err) {
      setSpeakingError(err instanceof Error ? err.message : 'Yêu cầu TTS thất bại');
    }
  };

  useEffect(() => {
    autoSpokenRef.current = false;
  }, [message.id]);

  useEffect(() => {
    if (autoSpeak && !autoSpokenRef.current && message.sender === 'ai' && textToSpeak && !message.restored) {
      autoSpokenRef.current = true;
      speaking.speakText(textToSpeak, message.id, { auto: true }).catch(() => {})
    }
  }, [autoSpeak, message.sender, message.id, textToSpeak, message.restored]);

  useEffect(() => {
    if (startSignal && message.sender === 'ai' && textToSpeak && !message.restored) {
      try { upsertMessage(message) } catch {}
      speaking.speakText(textToSpeak, message.id, { auto: false }).catch(() => {})
    }
  }, [startSignal]);

  return (
    <div 
      className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}
      role="article"
      aria-label={`${isUser ? 'Your' : 'AI'} message`}
    >
      {/* Avatar for AI messages (left side) */}
      {!isUser && showAvatar && (
        <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
          <Bot className="w-6 h-6 text-white" aria-hidden="true" />
        </div>
      )}

      {/* Message content */}
      <div className={`flex flex-col max-w-[80%] min-w-0 overflow-hidden ${isUser ? 'items-end' : 'items-start'}`}>
        {/* {!isUser && (
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={handleSpeak}
              disabled={isSpeaking || !getTextToSpeak().trim()}
              className={`w-7 h-7 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 ${isSpeaking ? 'cursor-wait' : ''}`}
              aria-label={isSpeaking ? 'Đang đọc...' : 'Đọc câu trả lời'}
              title={isSpeaking ? 'Đang đọc...' : 'Đọc câu trả lời hoặc gợi ý'}
            >
              <Volume2 className={`w-4 h-4 ${isSpeaking ? 'animate-pulse' : ''}`} />
            </button>
            {message.content && (
              <CopyButton 
                text={message.content}
                size="sm"
              />
            )}
          </div>
        )} */}
        {message.content && (
          <div className="flex items-start gap-2 w-full">
            <div className="relative group flex-1">
              <div
                className={`
                  px-4 py-3 rounded-2xl break-words overflow-wrap-anywhere
                  ${isUser 
                    ? 'bg-blue-600 text-white rounded-br-md' 
                    : 'bg-gray-100 text-gray-900 rounded-bl-md'
                  }
                  shadow-sm
                  transition-all duration-200 ease-in-out
                  hover:shadow-md
                  focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-opacity-50
                `}
                tabIndex={0}
                role="text"
              >
                {isUser ? (
                  <p className="text-base leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </p>
                ) : (
                  <div className="prose prose-sm max-w-full prose-gray overflow-hidden">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeHighlight]}
                      components={{
                        p: ({ children }) => (
                          <p className="mb-2 last:mb-0 text-gray-900 text-base leading-relaxed break-words overflow-wrap-anywhere">
                            {children}
                          </p>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc list-inside mb-2 last:mb-0 space-y-1">
                            {children}
                          </ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal list-inside mb-2 last:mb-0 space-y-1">
                            {children}
                          </ol>
                        ),
                        li: ({ children }) => (
                          <li className="text-gray-900">{children}</li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-gray-900">{children}</strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic text-gray-800">{children}</em>
                        ),
                        code: ({ children }) => (
                          <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">
                            {children}
                          </code>
                        ),
                        pre: ({ children }) => (
                          <pre className="bg-gray-200 text-gray-800 p-3 rounded-lg overflow-x-auto text-sm font-mono mb-2 last:mb-0">
                            {children}
                          </pre>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 mb-2 last:mb-0">
                            {children}
                          </blockquote>
                        ),
                        a: ({ href, children }) => (
                          <a 
                            href={href} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {children}
                          </a>
                        )
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
            {!isUser && (
              <div className="flex-shrink-0 mt-1">
                <button
                  type="button"
                  onClick={handleSpeak}
                  disabled={!textToSpeak}
                  className={`w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 ${speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'cursor-wait' : ''}`}
                  aria-label={speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'Đang đọc...' : 'Đọc câu trả lời'}
                  title={speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'Đang đọc...' : 'Đọc câu trả lời hoặc gợi ý'}
                >
                  <Volume2 className={`w-8 h-8 ${speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'animate-pulse' : ''}`} />
                </button>
                {speakingError && (
                  <div className="mt-2 text-xs text-red-600" role="alert">
                    {speakingError}
                  </div>
                )}
                {/* {lastTtsRequestId && (
                  <div className="mt-1 text-[10px] text-gray-400" aria-label="Mã yêu cầu TTS">
                    Yêu cầu TTS: {lastTtsRequestId}
                  </div>
                )} */}
              </div>
            )}
            
          </div>
        )}

        {/* AI message metadata and enhanced features */}
        {!isUser && (
          <div className="mt-2 space-y-3 w-full">
            {/* Confidence score - Hidden as requested */}
            {/* {message.confidence !== undefined && (
              <div className="flex justify-start">
                <ConfidenceScore confidence={message.confidence} />
              </div>
            )} */}

            {/* Documents section */}
            {message.documents && message.documents.length > 0 && (
              <DocumentsList documents={message.documents} />
            )}

            {/* Summary section - Show before suggestions for non-direct-answer responses */}
            {message.summary && (message.responseType === 'suggestions' || message.responseType === 'search_recommended') && (
              <div className="mt-4 w-full flex items-start gap-2">
                <div className="flex-1 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">Tóm tắt thủ tục</h4>
                      <div className="text-sm text-blue-700 leading-relaxed">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => (<p className="mb-2 last:mb-0">{children}</p>),
                            ul: ({ children }) => (<ul className="list-disc list-inside mb-2 last:mb-0 space-y-1">{children}</ul>),
                            ol: ({ children }) => (<ol className="list-decimal list-inside mb-2 last:mb-0 space-y-1">{children}</ol>),
                            li: ({ children }) => (<li className="text-blue-700">{children}</li>),
                            strong: ({ children }) => (<strong className="font-semibold text-blue-800">{children}</strong>),
                            em: ({ children }) => (<em className="italic text-blue-600">{children}</em>)
                          }}
                        >
                          {message.summary}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 mt-1">
                <button
                  type="button"
                  onClick={handleSpeak}
                  disabled={!textToSpeak}
                  className={`w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 ${speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'cursor-wait' : ''}`}
                  aria-label={speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'Đang đọc...' : 'Đọc tóm tắt'}
                  title={speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'Đang đọc...' : 'Đọc tóm tắt'}
                >
                  <Volume2 className={`w-8 h-8 ${speaking.isSpeaking && speaking.speakingMessageId === message.id ? 'animate-pulse' : ''}`} />
                </button>
                </div>
              </div>
            )}

            {/* Render suggestions based on response type */}
            {renderSuggestions()}

            {/* Search recommendations for low-confidence responses */}
            {message.responseType === 'search_recommended' && onSearchRecommendation && (
              <div className="mt-4">
                <SearchRecommendations
                  recommendations={[
                    'Thử các câu hỏi khác',
                    'Hỏi về các chủ đề liên quan'
                  ]}
                  onSearchRecommendation={onSearchRecommendation}
                  originalQuestion={message.content}
                />
              </div>
            )}
          </div>
        )}

        {/* Timestamp */}
        {showTimestamp && (
          <span 
            className="text-xs text-gray-500 mt-1 px-1"
            aria-label={`Message sent at ${formatTime(message.timestamp)}`}
          >
            {formatTime(message.timestamp)}
          </span>
        )}
      </div>

      {/* Avatar for user messages (right side) */}
      {isUser && showAvatar && (
        <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
          <User className="w-6 h-6 text-white" aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
